import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Topbar from '../../components/common/Topbar';
import Toast from '../../components/common/Toast';
import { showToast } from '../../store/uiSlice';
import { setBalance } from '../../store/tradeSlice';
import { fmtUSD } from '../../utils/formatters';
import api from '../../services/api';

const Transfer = () => {
  const dispatch = useDispatch();
  const { balance } = useSelector((state) => state.trade);
  const [amount, setAmount] = useState('');
  const [toAccount, setToAccount] = useState('secondary');
  const [reference, setReference] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const secondaryBalance = 5250;
  const demoBalance = 10000;
  const investmentBalance = 0;

  const getToAccountBalance = () => {
    if (toAccount === 'secondary') return secondaryBalance;
    if (toAccount === 'demo') return demoBalance;
    return investmentBalance;
  };

  const getToAccountName = () => {
    if (toAccount === 'secondary') return 'Secondary Account (MT5)';
    if (toAccount === 'demo') return 'Demo Account';
    return 'Investment Account';
  };

  const setPresetAmount = (preset) => {
    if (preset <= balance) setAmount(preset);
    else dispatch(showToast({ message: `Amount exceeds available balance (${fmtUSD(balance)})`, type: 'error' }));
  };

  const processTransfer = async () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum < 10) { dispatch(showToast({ message: 'Minimum transfer is $10.00', type: 'error' })); return; }
    if (amountNum > balance) { dispatch(showToast({ message: `Insufficient balance. Max transfer: ${fmtUSD(balance)}`, type: 'error' })); return; }
    setIsProcessing(true);
    try {
      const response = await api.post('/user/transfer', { amount: amountNum, toAccount, reference: reference || 'Internal transfer' });
      if (response.data.success) {
        dispatch(setBalance(response.data.newBalance));
        dispatch(showToast({ message: `Successfully transferred ${fmtUSD(amountNum)} to ${getToAccountName()}`, type: 'success' }));
        setAmount(''); setReference('');
      }
    } catch (error) { dispatch(showToast({ message: error.response?.data?.message || 'Transfer failed', type: 'error' })); }
    finally { setIsProcessing(false); }
  };

  const inputStyle = { background: 'var(--color-bgInput)', border: '1px solid var(--color-borderMedium)', color: 'var(--color-text)', outline: 'none' };
  const cardStyle = { background: 'var(--color-bgSurface)', border: '1px solid var(--color-border)' };

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg)' }}>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute w-[700px] h-[700px] rounded-full top-[-260px] right-[-200px] animate-float" style={{ background: 'radial-gradient(circle, rgba(30,58,95,0.04), transparent 65%)' }} />
        <div className="absolute w-[600px] h-[600px] rounded-full bottom-[-200px] left-[-160px] animate-float animation-delay-2000" style={{ background: 'radial-gradient(circle, rgba(91,33,182,0.03), transparent 65%)' }} />
      </div>
      <Topbar />

      <div className="relative z-10 pt-[52px] p-5 flex items-center justify-center min-h-[calc(100vh-52px)]">
        <div className="w-full max-w-[600px]">
          <div className="text-center mb-8 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)' }}>
              <i className="fas fa-right-left text-[9px]" style={{ color: 'var(--color-orange)' }} />
              <span className="text-[9px] font-mono tracking-wide" style={{ color: 'var(--color-orange)' }}>FUNDS</span>
            </div>
            <h1 className="font-syne text-3xl md:text-4xl font-extrabold mb-2" style={{ color: 'var(--color-text)' }}>
              Transfer <span style={{ background: 'linear-gradient(to right, var(--color-orange), var(--color-purple))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Between Accounts</span>
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-textSecondary)' }}>Move funds instantly between your trading accounts without any fees</p>
          </div>

          <div className="rounded-2xl overflow-hidden animate-slide-up animation-delay-100" style={cardStyle}>
            <div className="flex items-center gap-3 px-6 py-4 border-b" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-orange)' }}>
                <i className="fas fa-exchange-alt text-base" />
              </div>
              <div>
                <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>Internal Transfer</div>
                <div className="text-[9px] font-mono" style={{ color: 'var(--color-textDim)' }}>Instant and free transfers</div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center justify-between gap-4 p-4 rounded-xl" style={{ background: 'var(--color-bgCard)' }}>
                <div className="text-center flex-1">
                  <div className="text-[7px] font-mono uppercase tracking-wide" style={{ color: 'var(--color-textDim)' }}>FROM</div>
                  <div className="font-syne text-sm font-extrabold mt-1" style={{ color: 'var(--color-text)' }}>Trading Account</div>
                  <div className="font-mono text-[10px] font-bold mt-0.5" style={{ color: 'var(--color-green)' }}>{fmtUSD(balance)}</div>
                </div>
                <i className="fas fa-arrow-right text-xl" style={{ color: 'var(--color-orange)' }} />
                <div className="text-center flex-1">
                  <div className="text-[7px] font-mono uppercase tracking-wide" style={{ color: 'var(--color-textDim)' }}>TO</div>
                  <div className="font-syne text-sm font-extrabold mt-1" style={{ color: 'var(--color-text)' }}>{getToAccountName()}</div>
                  <div className="font-mono text-[10px] font-bold mt-0.5" style={{ color: 'var(--color-purpleLight)' }}>{fmtUSD(getToAccountBalance())}</div>
                </div>
              </div>

              <div>
                <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-orange)' }}>Transfer Amount</label>
                <div className="grid grid-cols-4 gap-2 mb-3">
                  {[100, 500, 1000, 5000].map(preset => (
                    <button key={preset} onClick={() => setPresetAmount(preset)}
                      className="py-1.5 rounded-lg font-mono text-[10px] font-semibold transition-all border"
                      style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)', color: 'var(--color-textMuted)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--color-orange)'; e.currentTarget.style.color = 'var(--color-orange)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-borderMedium)'; e.currentTarget.style.color = 'var(--color-textMuted)'; }}>
                      ${preset.toLocaleString()}
                    </button>
                  ))}
                </div>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-syne text-base font-extrabold" style={{ color: 'var(--color-orange)' }}>$</span>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" min="10" step="1"
                    className="w-full rounded-lg px-3 py-3 pl-7 font-dm text-sm" style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
                </div>
              </div>

              <div>
                <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-orange)' }}>To Account</label>
                <select value={toAccount} onChange={(e) => setToAccount(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 font-dm text-sm" style={inputStyle}>
                  <option value="secondary">Secondary Account (MT5) - {fmtUSD(secondaryBalance)}</option>
                  <option value="demo">Demo Account - {fmtUSD(demoBalance)}</option>
                  <option value="investment">Investment Account - {fmtUSD(investmentBalance)}</option>
                </select>
              </div>

              <div>
                <label className="text-[9px] font-mono tracking-wide uppercase block mb-2" style={{ color: 'var(--color-orange)' }}>Reference (Optional)</label>
                <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Internal transfer"
                  className="w-full rounded-lg px-3 py-2 font-dm text-sm" style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--color-orange)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-borderMedium)'} />
              </div>

              <div className="flex gap-2 p-3 rounded-lg" style={{ background: 'var(--color-greenBg)', border: '1px solid var(--color-greenBorder)' }}>
                <i className="fas fa-bolt text-sm" style={{ color: 'var(--color-green)' }} />
                <div className="text-[9px]" style={{ color: 'var(--color-textMuted)' }}>
                  <b style={{ color: 'var(--color-text)' }}>Instant & Free</b> — Transfers between your accounts are processed instantly with zero fees.
                </div>
              </div>

              <button onClick={processTransfer} disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full font-syne text-sm font-bold transition-all active:translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--color-gradientOrange)', color: 'var(--color-textInverse)' }}>
                {isProcessing ? <><span className="inline-block w-3 h-3 border-2 rounded-full animate-spin mr-1" style={{ borderColor: 'var(--color-textInverse)', borderTopColor: 'transparent' }} /> Processing</>
                  : <><i className="fas fa-right-left mr-1" /> Transfer Funds</>}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toast />
      <style>{`
        @keyframes float { 0%,100% { transform:translate(0,0) } 50% { transform:translate(28px,-28px) } }
        @keyframes slide-up { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
        .animate-float { animation:float 20s ease-in-out infinite }
        .animation-delay-2000 { animation-delay:-8s }
        .animate-slide-up { animation:slide-up .6s ease-out both }
        .animation-delay-100 { animation-delay:.1s }
      `}</style>
    </div>
  );
};

export default Transfer;